import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Search, UserRound } from "lucide-react";
import { CATEGORIES } from "../data/catalog";
import Logo from "./Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/catalogue", label: "Catalogue", hasMenu: true },
  { to: "/about", label: "About Us" },
  { to: "/useful-information", label: "Useful Information" },
  { to: "/where-to-find", label: "Where to Find" },
];

export const Header = () => {
  const [openCat, setOpenCat] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setOpenCat(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full bg-white transition-all ${
        scrolled ? "shadow-[0_1px_0_0_#CBD5E1]" : "border-b border-leduro-border"
      }`}
    >
      {/* Top utility strip */}
      <div className="hidden md:block bg-leduro-navy text-white text-xs">
        <div className="leduro-container flex h-9 items-center justify-between">
          <div className="flex items-center gap-6 font-medium tracking-wide">
            <span className="text-white/80">B2B Trade Portal · Baltics & EU</span>
            <span className="text-leduro-orange">●</span>
            <span className="text-white/80">In stock & ship in 24h</span>
          </div>
          <div className="flex items-center gap-5 font-medium">
            <a href="mailto:sales@leduro.eu" className="hover:text-leduro-orange transition" data-testid="header-email-link">
              sales@leduro.eu
            </a>
            <span className="text-white/30">|</span>
            <a href="tel:+37167000000" className="hover:text-leduro-orange transition" data-testid="header-phone-link">
              +371 67 000 000
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="leduro-container flex h-20 items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.hasMenu ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setOpenCat(true)}
                onMouseLeave={() => setOpenCat(false)}
              >
                <NavLink
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                      isActive ? "text-leduro-orange" : "text-leduro-navy hover:text-leduro-orange"
                    }`
                  }
                >
                  {item.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openCat ? "rotate-180" : ""}`} />
                </NavLink>
                {openCat && (
                  <div
                    data-testid="catalogue-megamenu"
                    className="absolute left-1/2 top-full -translate-x-1/2 w-[640px] border border-leduro-border bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)] animate-fade-up"
                  >
                    <div className="grid grid-cols-2">
                      {CATEGORIES.map((c, idx) => (
                        <Link
                          key={c.slug}
                          to={`/catalogue/${c.slug}`}
                          data-testid={`megamenu-cat-${c.slug}`}
                          className={`group flex items-center gap-4 px-5 py-4 border-leduro-border hover:bg-leduro-surface ${
                            idx % 2 === 0 ? "border-r" : ""
                          } ${idx < CATEGORIES.length - 2 ? "border-b" : ""}`}
                        >
                          <div className="h-12 w-12 shrink-0 bg-leduro-surface border border-leduro-border overflow-hidden">
                            <img src={c.image} alt={c.name} className="h-full w-full object-cover" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-bold text-leduro-navy group-hover:text-leduro-orange transition-colors">
                              {c.name}
                            </div>
                            <div className="text-xs text-leduro-muted truncate">{c.description.split(".")[0]}.</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <Link
                      to="/catalogue"
                      data-testid="megamenu-view-all"
                      className="block bg-leduro-navy text-white text-center text-xs font-semibold uppercase tracking-[0.2em] py-3 hover:bg-leduro-orange transition-colors"
                    >
                      View full catalogue →
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                    isActive ? "text-leduro-orange" : "text-leduro-navy hover:text-leduro-orange"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <button
            data-testid="header-search-btn"
            aria-label="Search"
            className="h-10 w-10 grid place-items-center border border-leduro-border text-leduro-navy hover:bg-leduro-navy hover:text-white transition-colors"
          >
            <Search className="h-4 w-4" />
          </button>
          <Link
            to="/login"
            data-testid="header-login-link"
            className="leduro-btn-primary !py-2.5 !px-4 text-xs"
          >
            <UserRound className="h-4 w-4" />
            LOG IN | REGISTER
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden h-10 w-10 grid place-items-center border border-leduro-border text-leduro-navy"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-leduro-border bg-white" data-testid="mobile-menu">
          <div className="leduro-container py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `block py-3 text-sm font-semibold uppercase tracking-wider border-b border-leduro-border ${
                    isActive ? "text-leduro-orange" : "text-leduro-navy"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-2">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/catalogue/${c.slug}`}
                  data-testid={`mobile-cat-${c.slug}`}
                  className="text-xs uppercase tracking-wider py-2 px-3 border border-leduro-border text-leduro-navy"
                >
                  {c.short}
                </Link>
              ))}
            </div>
            <Link to="/login" data-testid="mobile-login-link" className="leduro-btn-primary mt-4 justify-center">
              LOG IN | REGISTER
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
